import { useMemo, useState } from 'react';
import { Modal } from '../../../../components/Modal/Modal';
import { ModalParams } from '../../../../hooks/useModal';
import { GraniteButton } from '../../../../components/V2/Button/GraniteButton';
import Checkbox from 'components/Checkbox';
import { formatCurrency } from 'shared/util/formatCurrency';
import PriceBreakdown from './PriceBreakdown';
import { useForm } from 'react-hook-form';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccessReviewOrderForm,
  AccessReviewOrderFormSchema,
} from 'api/accessexpressv3/schemas/AccessCheckoutSchemas';
import Loader from 'components/Loader';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';

export interface AccessReviewOrderModalProps extends ModalParams {
  onConfirmation: (formData: AccessReviewOrderForm) => void;
}

// temp type for itemDetails
type TempItemDetailsType = {
  formal_broadband_product: {
    formal_broadband_pricing: {
      service_activation: number;
      modem: number;
      circuit: number;
      internet_access_recovery: number;
      ip: number;
      mns_essentials: number;
      mns_enhanced: number;
    };
  };
  mns_essential: boolean;
  mns_enhanced: boolean;
};

export const AccessReviewOrderModal = ({
  isOpen,
  close,
  onConfirmation,
}: AccessReviewOrderModalProps) => {
  const cart = useCartContext();
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<AccessReviewOrderForm>({
    resolver: zodResolver(AccessReviewOrderFormSchema),
  });

  const onSubmit = (data: AccessReviewOrderForm) => {
    if (onConfirmation) {
      onConfirmation(data);
    }
  };
  const services = useMemo(() => {
    const tempServices: string[] = [];
    cart.items.forEach((item) => {
      if (item.subCategory && !tempServices.includes(item.subCategory))
        tempServices.push(item.subCategory);
    });
    return tempServices;
  }, [cart.items]);

  const nonRecurringCharges = useMemo(() => {
    const { totalServiceActivation, totalModem } = cart.items.reduce(
      (totals, item) => {
        const itemDetails = item.item_details as
          | TempItemDetailsType
          | undefined;
        const serviceActivation = Number(
          itemDetails?.formal_broadband_product?.formal_broadband_pricing
            ?.service_activation ?? 0,
        );
        const modem = Number(
          itemDetails?.formal_broadband_product?.formal_broadband_pricing
            ?.modem ?? 0,
        );

        return {
          totalServiceActivation:
            totals.totalServiceActivation + serviceActivation,
          totalModem: totals.totalModem + modem,
        };
      },
      { totalServiceActivation: 0, totalModem: 0 },
    );

    const grandTotal = totalServiceActivation + totalModem;

    return {
      grandTotal,
      charges: [
        { name: 'Activation fee', price: totalServiceActivation },
        { name: 'Equipment', price: totalModem },
      ],
    };
  }, [cart.items]);

  const recurringCharges = useMemo(() => {
    const {
      totalCircuit,
      totalInternetAccessRecovery,
      totalIP,
      networkMonitoring,
    } = cart.items.reduce(
      (totals, item) => {
        const itemDetails = item.item_details as
          | TempItemDetailsType
          | undefined;
        const circuit = Number(
          itemDetails?.formal_broadband_product?.formal_broadband_pricing
            ?.circuit ?? 0,
        );
        const internetAccessRecovery = Number(
          itemDetails?.formal_broadband_product?.formal_broadband_pricing
            ?.internet_access_recovery ?? 0,
        );
        const ip = Number(
          itemDetails?.formal_broadband_product?.formal_broadband_pricing?.ip ??
            0,
        );

        const networkMonitoring = itemDetails?.mns_enhanced
          ? itemDetails?.formal_broadband_product?.formal_broadband_pricing
              ?.mns_enhanced
          : itemDetails?.mns_essential
            ? itemDetails?.formal_broadband_product?.formal_broadband_pricing
                ?.mns_essentials
            : 0;

        return {
          totalCircuit: totals.totalCircuit + circuit,
          totalInternetAccessRecovery:
            totals.totalInternetAccessRecovery + internetAccessRecovery,
          totalIP: totals.totalIP + ip,
          networkMonitoring: totals.networkMonitoring + networkMonitoring,
        };
      },
      {
        totalCircuit: 0,
        totalInternetAccessRecovery: 0,
        totalIP: 0,
        networkMonitoring: 0,
      },
    );

    const grandTotal =
      totalCircuit + totalInternetAccessRecovery + totalIP + networkMonitoring;

    return {
      grandTotal,
      charges: [
        { name: 'Broadband connection charge', price: totalCircuit },
        {
          name: 'Internet access recovery',
          price: totalInternetAccessRecovery,
        },
        { name: 'IP blocks', price: totalIP },
        { name: 'Network monitoring', price: networkMonitoring },
      ],
    };
  }, [cart.items]);

  return (
    <Modal
      isVisible={isOpen}
      close={close}
      className="vertical-scrollbar z-50 max-h-[90vh] max-w-[624px] overflow-y-auto"
    >
      <div className="flex flex-col gap-2 rounded-t-lg bg-background-base-surface-3 px-8 py-6">
        <h1 className="text-[28px] leading-9 text-content-base-default">
          Review & finalize order
        </h1>
        <p className="font-semibold leading-[22px] text-content-base-subdued">
          Take a moment to review the details of your order. This includes your
          selected plans, any additional services, and the total cost.
        </p>
      </div>
      <div className="flex flex-col p-8">
        <PriceBreakdown
          info={cart.locations.length}
          title="Total locations"
          className="rounded-t !p-4 !pb-0"
        >
          {!!cart.locations.length && (
            <div className="flex flex-col gap-[10px]">
              {cart.locations.map((location, index) => (
                <span
                  key={index}
                  className="text-base font-bold text-content-base-default"
                >
                  {index + 1}.&nbsp;
                  {[
                    location.address_line_1,
                    location.city,
                    location.state,
                    location.zip,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              ))}
            </div>
          )}
        </PriceBreakdown>
        <PriceBreakdown
          info={services.length}
          title="Total services"
          className="!p-4 !pb-0"
        >
          {!!services.length && (
            <div className="flex flex-col gap-[10px]">
              {services.map((service, index) => (
                <span
                  key={index}
                  className="text-base font-bold text-content-base-default"
                >
                  {service}
                </span>
              ))}
            </div>
          )}
        </PriceBreakdown>
        <PriceBreakdown
          info={
            <span className="font-bold text-content-accent-default">
              {formatCurrency(nonRecurringCharges.grandTotal)}
            </span>
          }
          title="Total non-recurring charges"
          className="!p-4 !pb-0"
        >
          <div className="flex flex-col gap-[10px] text-base font-bold">
            {nonRecurringCharges.charges.map((item, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-content-base-default">{item.name}</span>
                <span className="text-content-base-subdued">
                  {formatCurrency(item.price)}
                </span>
              </div>
            ))}
          </div>
        </PriceBreakdown>
        <PriceBreakdown
          info={
            <span className="font-bold text-content-accent-default">
              {formatCurrency(recurringCharges.grandTotal)}
            </span>
          }
          title="Total recurring charges"
          className="rounded-b !p-4"
        >
          <div className="flex flex-col gap-[10px] text-base font-bold">
            {recurringCharges.charges.map((item, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-content-base-default">{item.name}</span>
                <span className="text-content-base-subdued">
                  {formatCurrency(item.price)}
                </span>
              </div>
            ))}
          </div>
        </PriceBreakdown>
        <form
          className="mt-6 flex flex-col gap-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <GraniteInput
            label="Order name"
            subtitle="Assign a unique name to this order to help you recognize it later"
            placeholder="E.g., HQ Primary Circuit, Store #2 Access"
            variant="blue"
            className="col-span-1"
            innerInputClassName="!w-full !box-border"
            {...register(`order_name`)}
            error={errors.order_name?.message}
          />
          <p className="mt-8 text-base font-bold text-content-base-subdued">
            By clicking the following, you are confirming that you have read,
            understood, and agree to{' '}
            <a
              href={`https://www.granitenet.com/Content/pdfs/Granite%20Website%20-%20General%20Terms%20and%20Conditions%20of%20Service%20(May%2023,%202022)%20(00053066xD85FF).pdf`}
              target="_blank"
              rel="noreferrer"
              download
              className="cursor-pointer text-content-accent-default underline visited:text-content-accent-default"
            >
              {"Granite's"} Terms & Conditions.
            </a>
          </p>
          <Checkbox
            label="I agree to Granite’s Terms & Conditions"
            checked={isChecked}
            onChange={handleCheckboxChange}
          />
          <div className="mt-8 flex flex-col gap-5 md:flex-row">
            <GraniteButton size="large" variant="secondary" onClick={close}>
              Cancel
            </GraniteButton>
            <GraniteButton
              size="large"
              disabled={!isChecked || isSubmitting}
              type="submit"
            >
              Agree & submit{' '}
              {isSubmitting ? <Loader width="w-5" height="h-5" /> : null}
            </GraniteButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};
